import React, { ReactNode } from 'react';
import {
  Combobox,
  ComboboxOption,
  createStyles,
  Dialog,
  DialogContent,
  DialogTitle,
  makeStyles,
  Typography,
  DialogActions,
  DefaultButton,
  PrimaryButton,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';
import { IJobProvider, IJobSummary, JobDetailResponseBody } from '@lct-jobs/core';
import { IChangeEvent } from '@rjsf/core';
import useRestApi from '../../hooks/useRestApi';
import { useAppConfigContext } from 'contexts/AppConfigContext';
import { useJobRegion } from 'contexts/JobRegionContext';
import { DataSharingProvider } from 'contexts/DataSharingContext';
import { sanitizeDefaults } from './SanitizeDefaults';
import { JobForm } from './JobForm';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { Account } from '@bb-ui/icons/dist/small';

export interface JobSubmitEvent extends IChangeEvent<any> {
  selectedProvider?: string;
  scheduledStartTime?: string;
}

export interface RunDialogProps {
  jobSummary: IJobSummary;
  tenantId: string;
  closeDialog: () => void;
  submitJob: (s: JobSubmitEvent) => void;
}

type ProviderComboboxOption = ComboboxOption & {
  provider?: IJobProvider;
};

export const useStyles = makeStyles((theme) =>
  createStyles({
    form: {
      '& div[role="radiogroup"] label span:first-child': {
        width: '32px',
        height: '32px',
        borderRadius: '16px',
        '&:hover': {
          backgroundColor: theme.palette.background.b4,
        },
      },
    },
    loadingText: {
      paddingBottom: theme.spacing(1),
    },
    runByBox: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 2),
      marginBottom: theme.spacing(2.5),
      borderRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.background.b4,
    },
    runByLabel: {
      color: theme.palette.text.secondary,
    },
    runByValue: {
      fontWeight: 600,
    },
  }),
);

const confirmationJobIds = [
  'DecommissionLearnInstaller',
  'DecommissionLearnInstance',
  'DeleteCname',
  'DeleteDDA',
  'DeleteDDAUser',
];

const alphabeticalSort = (a: string, b: string) =>
  a.localeCompare(b, 'en-US', { sensitivity: 'base', numeric: true });

const gmtAwareSort = (a: string, b: string) => {
  const parseGmt = (str: string) => {
    if (typeof str !== 'string') return null;
    const match = str.match(/GMT([+-]?)(\d{1,2})?(?::(\d{2}))?/);
    if (!match) return null;

    if (!match[1] && !match[2]) return 0;

    const sign = match[1] === '-' ? -1 : 1;
    const hours = match[2] ? parseInt(match[2], 10) : 0;
    const mins = match[3] ? parseInt(match[3], 10) : 0;

    return sign * (hours * 60 + mins);
  };

  const valA = parseGmt(a);
  const valB = parseGmt(b);

  if (valA !== null && valB !== null) {
    if (valA !== valB) {
      return valA - valB;
    }
  }

  return alphabeticalSort(a, b);
};

const sortStaticEnums = (properties: Record<string, any>) => {
  for (const property of Object.values(properties)) {
    // SKIP properties that have autofillKey (backend already sorts them)
    if (property.autofillKey) {
      // eslint-disable-next-line no-continue
      continue;
    }

    // Only sort static enums (like Timezone)
    if (Array.isArray(property.enum)) {
      property.enum = [...property.enum].sort((a, b) => gmtAwareSort(String(a), String(b)));
    }
    if (Array.isArray(property.oneOf)) {
      property.oneOf = [...property.oneOf].sort((a: any, b: any) =>
        gmtAwareSort(a.title ?? '', b.title ?? ''),
      );
    }
    if (Array.isArray(property.examples)) {
      property.examples = [...property.examples].sort((a, b) => gmtAwareSort(String(a), String(b)));
    }
  }
};

export const RunDialog: React.FC<RunDialogProps> = (props) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { jobSummary } = props;
  const env = useAppConfigContext();
  const { lctApiUrl } = useJobRegion();
  const classes = useStyles(props);
  const [open, setOpen] = React.useState<boolean>(true);
  const [providerOptions, setProviderOptions] = React.useState<ProviderComboboxOption[]>([]);
  const [selectedProvider, setSelectedProvider] = React.useState<
    ProviderComboboxOption | undefined
  >();
  const [formData, setFormData] = React.useState<any>({});
  const [lockedFields, setLockedFields] = React.useState<Set<string>>(new Set());
  const lastAutofillSelectionRef = React.useRef<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = React.useState<boolean>(false);
  const [pendingSubmit, setPendingSubmit] = React.useState<JobSubmitEvent | null>(null);
  const [updatedSchema, setUpdatedSchema] = React.useState<any>(null);

  const { fetch, loading, data } = useRestApi<JobDetailResponseBody>(
    lctApiUrl(`tenants/${props.tenantId}/jobManager/jobs/${jobSummary.jobId}/providers`),
  );

  const [isPolling, setIsPolling] = React.useState(true);
  const intervalIdRef = React.useRef<NodeJS.Timeout | null>(null);
  // Track whether initial data has been received so subsequent fetches
  // are treated as background refreshes rather than full loading states.
  const hasInitialDataRef = React.useRef(false);
  // Keep a ref to the latest data so the interval callback can read it
  // without needing data in the polling effect's dependency array.
  const dataRef = React.useRef(data);
  React.useEffect(() => {
    dataRef.current = data;
    if (data && !hasInitialDataRef.current) {
      hasInitialDataRef.current = true;
    }
  }, [data]);

  const environment = env.stage;

  // Only block form rendering on the very first load; background polls use isRefreshing instead.
  const isInitialLoading = loading && !hasInitialDataRef.current;
  const canRenderForm = !isInitialLoading && selectedProvider && updatedSchema;

  // Function to update properties conditionally based on environment rules
  const applyEnvironmentConditionalRules = (properties: Record<string, any>, env: string) => {
    const newProperties = { ...properties };
    for (const [propKey, property] of Object.entries(newProperties)) {
      if (property.conditionalField) {
        const { allowedEnvironments } = property.conditionalField;
        const isEnvAllowed =
          Array.isArray(allowedEnvironments) &&
          allowedEnvironments
            .map((envStr: string) => envStr.toLowerCase())
            .includes(env.toLowerCase());
        if (!isEnvAllowed) {
          delete newProperties[propKey];
        }
      }
    }
    return newProperties;
  };

  const updatePropertiesWithResolvedKeys = (
    resolvedKeys: Record<string, any>,
    properties: Record<string, any>,
  ) => {
    for (const [key, resolvedKey] of Object.entries(resolvedKeys)) {
      for (const [propKey, property] of Object.entries(properties)) {
        if (property.autofillKey === key) {
          if (resolvedKey.defaultValue) {
            property.default = resolvedKey.defaultValue;
          }
          if (resolvedKey.examples) {
            property.examples = resolvedKey.examples;
          }
          if (resolvedKey.enum) {
            // eslint-disable-next-line prefer-destructuring
            property.default = Object.keys(resolvedKey.enum)[0];
            const resolveValue = (val: any) =>
              val && typeof val === 'object' && ('value' in val || 'override' in val)
                ? val.value
                : val;
            const firstEnumValue =
              resolveValue(resolvedKey.enum[property.default][propKey]) ?? property.default;
            if (firstEnumValue !== property.default) {
              property.oneOf = Object.keys(resolvedKey.enum).map((enumKey) => ({
                title: enumKey,
                const: resolveValue(resolvedKey.enum[enumKey][propKey]) ?? enumKey,
              }));
            } else {
              property.enum = Object.keys(resolvedKey.enum);
            }

            for (const enumEntry of Object.values(resolvedKey.enum) as Record<string, any>[]) {
              for (const [depFieldKey, depValue] of Object.entries(enumEntry)) {
                if (
                  depValue &&
                  typeof depValue === 'object' &&
                  'override' in depValue &&
                  depValue.override
                ) {
                  const depProperty = properties[depFieldKey];
                  if (depProperty?.pattern && !depProperty.pattern.startsWith('^$|')) {
                    depProperty.pattern = `^$|${depProperty.pattern}`;
                  }
                }
              }
            }
          }
        }
      }
    }
  };

  const updateFormDataWithEnum = (
    properties: Record<string, any>,
    formData: any,
    newFormData: any,
    resolvedKeys: Record<string, any>,
  ) => {
    const updatedFormData = { ...newFormData };
    const newLockedFields = new Set<string>();
    for (const [fieldKey, property] of Object.entries(properties)) {
      const { autofillKey } = property;
      if (
        autofillKey &&
        resolvedKeys?.[autofillKey] &&
        formData[fieldKey] !== undefined &&
        formData[fieldKey] !== null &&
        formData[fieldKey] !== ''
      ) {
        const resolvedKey = resolvedKeys[autofillKey];
        const selectedValue = formData[fieldKey];
        const selectionChanged = lastAutofillSelectionRef.current[fieldKey] !== selectedValue;
        if (selectionChanged) {
          lastAutofillSelectionRef.current[fieldKey] = selectedValue;
        }
        const enumValue =
          resolvedKey.enum?.[selectedValue] ??
          Object.values(resolvedKey.enum ?? {}).find((v: any) => v[fieldKey] === selectedValue);
        if (enumValue) {
          for (const [enumFieldKey, value] of Object.entries(enumValue)) {
            if (enumFieldKey !== fieldKey) {
              if (value && typeof value === 'object' && 'override' in value) {
                const { value: resolvedValue, override } = value as {
                  value?: any;
                  override?: boolean;
                };
                if (override) {
                  if (selectionChanged) {
                    if (resolvedValue !== undefined) {
                      updatedFormData[enumFieldKey] = resolvedValue;
                    } else {
                      delete updatedFormData[enumFieldKey];
                    }
                  } else if (resolvedValue !== undefined && !(enumFieldKey in formData)) {
                    updatedFormData[enumFieldKey] = resolvedValue;
                  }
                } else {
                  updatedFormData[enumFieldKey] = resolvedValue;
                  newLockedFields.add(enumFieldKey);
                }
              } else {
                updatedFormData[enumFieldKey] = value;
                newLockedFields.add(enumFieldKey);
              }
            }
          }
        }
      }
    }
    setLockedFields(newLockedFields);
    return updatedFormData;
  };

  // useEffect for polling providers data — runs once on mount.
  // dataRef is used inside the interval so 'data' does not need to be
  // a dependency, which previously caused a new interval to be created
  // on every fetch response.
  React.useEffect(() => {
    const pollInterval = 10000;
    const pollDuration = 60000;

    intervalIdRef.current = setInterval(async () => {
      const currentData = dataRef.current;
      if (
        isPolling &&
        currentData?.outstandingAutoFillKeys &&
        currentData.outstandingAutoFillKeys.length > 0
      ) {
        await fetch();
      } else if (
        currentData?.outstandingAutoFillKeys &&
        currentData.outstandingAutoFillKeys.length === 0
      ) {
        setIsPolling(false);
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
        }
      }
    }, pollInterval);

    const timeoutId = setTimeout(() => {
      setIsPolling(false);
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    }, pollDuration);

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally run once — dataRef always holds the latest data

  // useEffect to close the dialog when open state is false
  React.useEffect(() => {
    if (!open) {
      props.closeDialog();
    }
  }, [open, props]);

  // useEffect to update provider options when provider data changes
  React.useEffect(() => {
    if (data?.providers) {
      const providers: IJobProvider[] = Object.values(data.providers);
      let defaultProvider: ProviderComboboxOption | undefined;

      const providerOpts = providers.map((provider: IJobProvider) => {
        const option = {
          label: provider.displayName ?? `${provider.region} ${provider.deployment ?? ''}`,
          value: provider.providerId,
          provider,
        };

        if (environment && provider.stage === environment) {
          defaultProvider = option;
        }

        return option;
      });

      setProviderOptions(providerOpts);

      if (defaultProvider) {
        setSelectedProvider(defaultProvider);
      } else if (providerOpts.length > 0) {
        setSelectedProvider(providerOpts[0]);
      }
    }
  }, [data?.providers, environment]);

  // useEffect to update the JSON schema based on resolved keys and environment conditions
  React.useEffect(() => {
    if (selectedProvider) {
      const inputSchemaWithProperties = selectedProvider.provider?.inputSchema as {
        properties: { [key: string]: any };
        required?: string[];
      };

      const newSchema = {
        ...inputSchemaWithProperties,
        properties: { ...inputSchemaWithProperties.properties },
      };

      if (environment && newSchema.properties) {
        newSchema.properties = applyEnvironmentConditionalRules(newSchema.properties, environment);
      }

      if (data?.resolvedAutoFillKeys && newSchema.properties) {
        updatePropertiesWithResolvedKeys(data.resolvedAutoFillKeys, newSchema.properties);
      }

      sanitizeDefaults(newSchema.properties);
      sortStaticEnums(newSchema.properties);

      setUpdatedSchema(newSchema);
    }
  }, [selectedProvider, data?.resolvedAutoFillKeys, environment, props.jobSummary.jobId]);

  const handleFormChange = ({ formData }: { formData: any }) => {
    if (!updatedSchema) return;

    setFormData((prevFormData: any) => {
      const newFormData = { ...prevFormData, ...formData };
      const schemaWithProperties = updatedSchema as { properties?: Record<string, any> };
      if (!schemaWithProperties.properties) return newFormData;

      const updated = updateFormDataWithEnum(
        schemaWithProperties.properties,
        formData,
        newFormData,
        data?.resolvedAutoFillKeys ?? {},
      );
      return updated;
    });
  };

  const handleSubmit = (s: IChangeEvent<any> & { scheduledStartTime?: string }) => {
    const requiresConfirmation = confirmationJobIds.includes(props.jobSummary.jobId);

    const cleanedFormData = Object.fromEntries(
      Object.entries(s.formData ?? {}).filter(([, v]) => v !== '' && v !== undefined && v !== null),
    );

    const submitEvent = {
      ...s,
      formData: cleanedFormData,
      selectedProvider: selectedProvider?.provider?.providerId,
    };

    if (requiresConfirmation) {
      setPendingSubmit(submitEvent);
      setConfirmOpen(true);
    } else {
      props.submitJob(submitEvent);
      setOpen(false);
    }
  };

  const handleConfirmSubmit = () => {
    if (pendingSubmit) {
      props.submitJob(pendingSubmit);
      setConfirmOpen(false);
      setPendingSubmit(null);
      setOpen(false);
    }
  };

  const handleCancelSubmit = () => {
    setConfirmOpen(false);
    setPendingSubmit(null);
  };

  return (
    <>
      <Dialog id="run-dialog" open={open} onClose={() => setOpen(false)}>
        <DialogTitle onClose={() => setOpen(false)} closeLabel={t('global.close')}>
          {jobSummary.jobName}
        </DialogTitle>
        <DialogContent>
          {currentUser?.email && (
            <div className={classes.runByBox}>
              <Account color="primary" />
              <Typography variant="body2" className={classes.runByLabel}>
                Running as <span className={classes.runByValue}>{currentUser.email}</span>
              </Typography>
            </div>
          )}
          {isInitialLoading && (
            <Typography variant="body2" color="textSecondary" className={classes.loadingText}>
              {t('jobService.loadingProviders')}
            </Typography>
          )}

          {/* Only show provider selection when data is loaded */}
          {!isInitialLoading && (environment === 'dev' || environment === 'local') && (
            <Combobox
              floatingLabel
              id="combobox-single"
              label="Select a Provider"
              placeholder="Default Provider"
              strings={{
                announceOptionSelected(option: ComboboxOption): string {
                  return `${option.label}, selected.`;
                },
                announceOptionDeselected(option: ComboboxOption): string {
                  return `${option.label}, deselected.`;
                },
                announceValueCleared: 'All values deselected.',
                announceSearchResults(count: number, searchString: string): string {
                  return `${count} results found for "${searchString}". Use up and down arrow keys to navigate. Press Enter to select.`;
                },
                clearButtonLabel: 'Clear',
                searchLabel: 'Search',
                noResults(searchString: string): ReactNode {
                  return `No results found for "${searchString}"`;
                },
              }}
              options={providerOptions}
              onChange={setSelectedProvider}
              isLoading={isInitialLoading}
              defaultValue={selectedProvider}
              ListboxProps={{
                style: {
                  maxHeight: '105px',
                  overflow: 'auto',
                },
              }}
            />
          )}

          {canRenderForm && (
            <DataSharingProvider jobId={jobSummary.jobId} tenantId={props.tenantId}>
              <JobForm
                formSchema={updatedSchema}
                formData={formData}
                onFormChange={handleFormChange}
                onFormSubmit={handleSubmit}
                selectedProvider={selectedProvider}
                className={classes.form}
                lockedFields={lockedFields}
              />
            </DataSharingProvider>
          )}

          {!canRenderForm && !isInitialLoading && (
            <Typography variant="body2" color="error">
              Unable to load form schema.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation dialog remains the same */}
      <Dialog
        open={confirmOpen}
        onClose={handleCancelSubmit}
        BackdropProps={{
          style: { backgroundColor: 'rgba(0, 0, 0, .75)' },
        }}
      >
        <DialogTitle onClose={handleCancelSubmit} closeLabel={t('global.close')}>
          {t('jobService.confirmSubmitTitle', { jobName: jobSummary.jobName })}
        </DialogTitle>
        <DialogActions>
          <DefaultButton onClick={handleCancelSubmit}>{t('global.cancel')}</DefaultButton>
          <PrimaryButton onClick={handleConfirmSubmit}>{t('global.submit')}</PrimaryButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
